import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { LoadingSpinner } from 'components/LoadingSpinner';
import { REAL_ESTATE_SEARCH_MIN_LENGTH, useRealEstates } from 'hooks/fetch/useRealEstates';
import { useRealEstateUsers } from 'hooks/fetch/useRealEstateUsers';
import { FormControl } from 'styles/FormControl';
import { InputWrapper } from 'styles/InputWrapper';
import { RealEstate, RealEstateUser } from 'types/RealEstate';

import {
  AdminBadge,
  Container,
  CurrentLinkHint,
  Dropdown,
  DropdownHeader,
  EmptyMessage,
  LinkActions,
  LoadingIcon,
  Option,
  SelectedLink,
} from './styles';
import { RealEstateUserPickerProps } from './types';

export function realEstateLabel(realEstate: RealEstate) {
  return `${realEstate.serial} - ${realEstate.name ?? 'Sem nome'}`;
}

/** Espaço mínimo, em pixels, para abrir a lista abaixo do campo */
const MIN_DROPDOWN_HEIGHT = 180;
const MAX_DROPDOWN_HEIGHT = 340;
const DROPDOWN_GAP = 4;

type PickedLink = {
  realEstate: RealEstate;
  user: RealEstateUser | null;
}

type DropdownPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function RealEstateUserPicker({
  realEstateId,
  userCompanyId,
  selectedRealEstateLabel,
  selectedUserLabel,
  onChange,
}: RealEstateUserPickerProps) {
  const [isSearching, setIsSearching] = useState(!realEstateId);
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [openRealEstate, setOpenRealEstate] = useState<RealEstate | null>(null);
  const [picked, setPicked] = useState<PickedLink | null>(null);
  const [position, setPosition] = useState<DropdownPosition | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: realEstates, isFetching } = useRealEstates(search);
  const { data: users, isFetching: isFetchingUsers } = useRealEstateUsers(openRealEstate?.id ?? null);

  const isDropdownOpen = isSearching && (!!openRealEstate || inputValue.length > 0);

  // Reinicia o campo quando o vínculo muda por fora do componente: troca de
  // contato, modal reaberta ou formulário resetado.
  useEffect(() => {
    if (picked?.realEstate.id === realEstateId) return;

    setPicked(null);
    setIsSearching(!realEstateId);
    setOpenRealEstate(null);
    setInputValue('');
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realEstateId]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const closeDropdown = useCallback(() => {
    setOpenRealEstate(null);
    setInputValue('');
    setSearch('');

    if (realEstateId) setIsSearching(false);
  }, [realEstateId]);

  // A lista é renderizada em um portal para não ser recortada pela modal,
  // por isso a posição precisa acompanhar o campo.
  useLayoutEffect(() => {
    if (!isDropdownOpen) {
      setPosition(null);
      return;
    }

    function updatePosition() {
      const field = containerRef.current;
      if (!field) return;

      const rect = field.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP * 2;
      const spaceAbove = rect.top - DROPDOWN_GAP * 2;
      const shouldOpenAbove = spaceBelow < MIN_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
      const availableHeight = Math.min(MAX_DROPDOWN_HEIGHT, shouldOpenAbove ? spaceAbove : spaceBelow);

      setPosition({
        top: shouldOpenAbove ? rect.top - availableHeight - DROPDOWN_GAP : rect.bottom + DROPDOWN_GAP,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
      });
    }

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    function handleClickOutside(event: Event) {
      const target = event.target as Node;

      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;

      closeDropdown();
    }

    function handleHideOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') closeDropdown();
    }

    document.addEventListener('keydown', handleHideOnEscape, true);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handleHideOnEscape, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isDropdownOpen, closeDropdown]);

  function handleChangeSearch(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    setInputValue(value);
    setOpenRealEstate(null);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setSearch(value.trim()), 500);
  }

  function handleSelect(realEstate: RealEstate, user: RealEstateUser | null) {
    setPicked({ realEstate, user });
    setOpenRealEstate(null);
    setIsSearching(false);
    setInputValue('');
    setSearch('');

    onChange({ real_estate_id: realEstate.id, user_company_id: user?.id ?? null });
  }

  function handleRemoveLink() {
    setPicked(null);
    onChange({ real_estate_id: null, user_company_id: null });
  }

  const currentRealEstateLabel = picked
    ? realEstateLabel(picked.realEstate)
    : selectedRealEstateLabel ?? 'Imobiliária vinculada';

  const currentUserLabel = userCompanyId
    ? (picked ? picked.user?.name : selectedUserLabel) ?? 'Usuário vinculado'
    : 'Apenas a imobiliária';

  function renderSelectedLink() {
    return (
      <SelectedLink>
        <div>
          <strong>{currentRealEstateLabel}</strong>
          <span>{currentUserLabel}</span>
        </div>

        <LinkActions>
          <button type="button" onClick={() => setIsSearching(true)}>Alterar</button>
          <button type="button" onClick={handleRemoveLink}>Remover</button>
        </LinkActions>
      </SelectedLink>
    );
  }

  function renderUsers(realEstate: RealEstate) {
    return (
      <>
        <DropdownHeader>
          <span>{realEstateLabel(realEstate)}</span>
          <button type="button" onClick={() => setOpenRealEstate(null)}>Voltar</button>
        </DropdownHeader>

        <Option type="button" highlighted onClick={() => handleSelect(realEstate, null)}>
          Vincular apenas a imobiliária
        </Option>

        {isFetchingUsers && <EmptyMessage>Carregando usuários...</EmptyMessage>}

        {!isFetchingUsers && users?.map(user => (
          <Option key={user.id} type="button" onClick={() => handleSelect(realEstate, user)}>
            {user.name}
            {user.is_admin && <AdminBadge>admin</AdminBadge>}
            <span>{user.email ?? 'Sem e-mail'}</span>
          </Option>
        ))}

        {!isFetchingUsers && !users?.length && (
          <EmptyMessage>Nenhum usuário ativo nesta imobiliária.</EmptyMessage>
        )}
      </>
    );
  }

  function renderRealEstates() {
    if (inputValue.trim().length < REAL_ESTATE_SEARCH_MIN_LENGTH) {
      return <EmptyMessage>Digite ao menos {REAL_ESTATE_SEARCH_MIN_LENGTH} caracteres.</EmptyMessage>;
    }

    return (
      <>
        {realEstates?.map(realEstate => (
          <Option key={realEstate.id} type="button" onClick={() => setOpenRealEstate(realEstate)}>
            {realEstateLabel(realEstate)}
          </Option>
        ))}

        {!isFetching && !realEstates?.length && (
          <EmptyMessage>Nenhuma imobiliária encontrada.</EmptyMessage>
        )}
      </>
    );
  }

  function renderDropdown() {
    if (!isDropdownOpen || !position) return null;

    return createPortal(
      <Dropdown
        ref={dropdownRef}
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          maxHeight: position.maxHeight,
        }}
      >
        {openRealEstate ? renderUsers(openRealEstate) : renderRealEstates()}
      </Dropdown>,
      document.body,
    );
  }

  function renderSearch() {
    return (
      <>
        {realEstateId && (
          <CurrentLinkHint>
            <span>
              Mantendo <strong>{currentRealEstateLabel}</strong> ({currentUserLabel}) até você escolher outro
            </span>
            <button type="button" onClick={() => setIsSearching(false)}>Cancelar</button>
          </CurrentLinkHint>
        )}

        <InputWrapper>
          {isFetching && (
            <LoadingIcon>
              <LoadingSpinner size={15} thickness={1} />
            </LoadingIcon>
          )}

          <FormControl
            as="input"
            type="text"
            id="real_estate_search"
            autoComplete="off"
            placeholder="Serial ou nome da imobiliária"
            value={inputValue}
            onChange={handleChangeSearch}
            fullwidth
          />
        </InputWrapper>

        {renderDropdown()}
      </>
    );
  }

  return (
    <Container ref={containerRef}>
      <label htmlFor="real_estate_search">Imobiliária</label>

      {realEstateId && !isSearching ? renderSelectedLink() : renderSearch()}
    </Container>
  );
}
